import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Check, AlertCircle } from "lucide-react";
import { COMPANY, INTERVENTION_TYPES, INTERVENTION_CITIES } from "../lib/constants";
import useSeo, { breadcrumbJsonLd, SITE } from "../lib/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  nom: "",
  telephone: "",
  email: "",
  ville: "",
  type_intervention: "",
  message: "",
};

export default function Contact() {
  useSeo({
    title: "Contact — Devis gratuit serrurier Bretagne | Mon Serrurier Breton",
    description:
      "Contactez votre serrurier breton à Mont-Dol. Devis gratuit en quelques minutes, intervention 24h/24. Téléphone 07 80 93 93 00, formulaire en ligne, secteur 22 & 35.",
    path: "/contact",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Accueil", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "@id": `${SITE.url}/contact#page`,
        name: "Contact — Mon Serrurier Breton",
        url: `${SITE.url}/contact`,
        inLanguage: "fr-FR",
        about: { "@id": `${SITE.url}/#business` },
        mainEntity: {
          "@type": "Organization",
          "@id": `${SITE.url}/#organization`,
          name: "Mon Serrurier Breton",
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+33780939300",
              contactType: "customer service",
              areaServed: "FR",
              availableLanguage: ["French"],
              hoursAvailable: {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday",
                ],
                opens: "00:00",
                closes: "23:59",
              },
            },
            {
              "@type": "ContactPoint",
              email: "contact@monserrurierbreton.fr",
              contactType: "customer support",
              areaServed: "FR",
              availableLanguage: ["French"],
            },
          ],
        },
      },
    ],
  });
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.nom || form.nom.trim().length < 2) e.nom = "Nom requis";
    if (!form.telephone || form.telephone.trim().length < 6)
      e.telephone = "Téléphone requis";
    if (!form.message || form.message.trim().length < 2)
      e.message = "Message requis";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Email invalide";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.ville) delete payload.ville;
      if (!payload.type_intervention) delete payload.type_intervention;
      await axios.post(`${API}/contact`, payload);
      toast.success("Demande envoyée — nous vous rappelons rapidement !");
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Erreur à l'envoi. Merci d'appeler directement.";
      toast.error(typeof msg === "string" ? msg : "Erreur d'envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-10">
        <div className="max-w-3xl space-y-5">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700">
            <span className="h-px w-8 bg-teal-700" /> Contact
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
            Parlons de vos besoins
            <br />
            en <span className="text-teal-600">serrurerie</span>.
          </h1>
          <p className="text-slate-600 leading-relaxed max-w-2xl">
            Pour toute urgence, privilégiez l'appel — nous sommes disponibles
            24h/24, 7j/7. Pour un devis ou une question, utilisez le
            formulaire : nous vous recontactons rapidement.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: info cards */}
          <div className="lg:col-span-5 space-y-5">
            <a
              data-testid="contact-card-phone"
              href={`tel:${COMPANY.phoneDigits}`}
              className="block rounded-3xl bg-orange-600 text-white p-7 hover:bg-orange-700 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-orange-100 font-bold">
                    Urgence — à toute heure
                  </div>
                  <div className="font-display text-3xl md:text-4xl font-extrabold mt-1">
                    {COMPANY.phone}
                  </div>
                  <div className="text-orange-100 text-sm mt-1">
                    Intervention moyenne sous 30 min
                  </div>
                </div>
              </div>
            </a>

            <div className="rounded-3xl bg-white border border-slate-200 p-7 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-slate-900">Horaires</div>
                  <div className="text-slate-600 text-sm">
                    7j/7, 24h/24 — dépannage urgence<br />
                    Devis & rendez-vous du lundi au samedi
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-slate-900">Zone</div>
                  <div className="text-slate-600 text-sm">
                    Mont-Dol & {COMPANY.radiusKm} km autour —
                    Secteur 22 & 35
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-bold text-slate-900">Email</div>
                  <div className="text-slate-600 text-sm break-all">
                    {COMPANY.email}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-slate-900 text-white p-7">
              <div className="text-xs uppercase tracking-widest text-teal-300 font-bold mb-2">
                Bon à savoir
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                Tarifs annoncés <strong className="text-white">avant</strong>{" "}
                intervention. Facture détaillée pour{" "}
                <strong className="text-white">remboursement assurance</strong>.
                Artisan diplômé, matériel certifié.
              </p>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              noValidate
              className="bg-white border border-slate-200 rounded-3xl p-7 md:p-10 space-y-5"
            >
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">
                Demande de devis ou question
              </h2>

              {sent && (
                <div
                  data-testid="contact-success"
                  className="flex items-start gap-3 bg-teal-50 border border-teal-200 text-teal-900 rounded-2xl p-4"
                >
                  <Check className="w-5 h-5 mt-0.5" />
                  <div className="text-sm">
                    <strong>Merci !</strong> Votre demande a été envoyée, nous
                    vous rappelons rapidement. Pour une urgence, appelez le{" "}
                    <a
                      href={`tel:${COMPANY.phoneDigits}`}
                      className="underline font-bold"
                    >
                      {COMPANY.phone}
                    </a>
                    .
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Nom / Prénom *" error={errors.nom}>
                  <input
                    data-testid="contact-input-nom"
                    type="text"
                    value={form.nom}
                    onChange={update("nom")}
                    className={inputCls(errors.nom)}
                    placeholder="Jean Dupont"
                    required
                  />
                </Field>
                <Field label="Téléphone *" error={errors.telephone}>
                  <input
                    data-testid="contact-input-telephone"
                    type="tel"
                    value={form.telephone}
                    onChange={update("telephone")}
                    className={inputCls(errors.telephone)}
                    placeholder="06 12 34 56 78"
                    required
                  />
                </Field>
                <Field label="Email" error={errors.email}>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className={inputCls(errors.email)}
                    placeholder="jean@exemple.fr"
                  />
                </Field>
                <Field label="Ville">
                  <input
                    data-testid="contact-input-ville"
                    list="villes-list"
                    type="text"
                    value={form.ville}
                    onChange={update("ville")}
                    className={inputCls()}
                    placeholder="Mont-Dol"
                  />
                  <datalist id="villes-list">
                    {INTERVENTION_CITIES.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </Field>
              </div>

              <Field label="Type d'intervention">
                <select
                  data-testid="contact-input-type"
                  value={form.type_intervention}
                  onChange={update("type_intervention")}
                  className={inputCls()}
                >
                  <option value="">— Sélectionnez (optionnel) —</option>
                  {INTERVENTION_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Votre message *" error={errors.message}>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  className={inputCls(errors.message)}
                  placeholder="Décrivez brièvement votre situation (porte claquée, devis blindage…)."
                  required
                />
              </Field>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-slate-500 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Pour toute urgence, préférez l'appel —
                  disponibilité 24h/24.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-teal-600 text-white font-bold rounded-full px-8 py-4 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  {loading ? "Envoi..." : "Envoyer la demande"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-bold text-slate-800 mb-1.5">
        {label}
      </span>
      {children}
      {error && (
        <span className="block text-xs text-red-600 mt-1 font-semibold">
          {error}
        </span>
      )}
    </label>
  );
}

function inputCls(err) {
  return `w-full bg-slate-50 border rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 outline-none transition-colors ${
    err
      ? "border-red-400 focus:border-red-500 focus:bg-white"
      : "border-slate-200 focus:border-teal-500 focus:bg-white"
  }`;
}
